// dependencies
import React, { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { useWindowSize } from "usehooks-ts";

// components
import ComponentLoader from "../../../components/Spinner/ComponentLoader";

// hooks
import { useRbacPrimaryRolePermissions } from "../../../api/hooks";
import { useFeatureFlagQuery } from "../../../hooks/useFeatureFlag";

// selectors
import { getUserID } from "../../../selectors/app";

// sub pages
import Alignment from "./SubPages/Alignment";
import EmployeesWithoutGoals from "./SubPages/EmployeesWithoutGoals";
import GoalsList from "./SubPages/GoalsList";
import Overview from "./SubPages/Overview";
// constants
import { GOALS_SIDE_NAVIGATION_LINKS, GoalsPage } from "./constants";

const CompanyGoalsPage = () => {
	const { page } = useParams<{ page: GoalsPage }>();
	const { width } = useWindowSize();

	const userID = useSelector((state) => getUserID(state));
	const { data: permissions, isLoading } = useRbacPrimaryRolePermissions("goal");
	const { value: ffGoalsDashboard } = useFeatureFlagQuery("ff-is-goal-overview-and-alignment-enabled", false);
	const userCanViewCompanyGoalsList = permissions?.includes("goals:viewCompanyGoalsList") || false;
	const userCanViewGoalsOverview = permissions?.includes("goals:viewGoalsOverviewDashboard") || false;
	const userCanViewGoalsAlignment = permissions?.includes("goals:viewGoalsAlignmentTree") || false;
	const userCanExportGoalToCsv = permissions?.includes("goals:exportGoalCsv") || false;
	const userCanBulkEditGoals = permissions?.includes("goals:bulkEditGoals") || false;
	const userCanViewCompanyUsersWithoutGoalsList =
		permissions?.includes("goals:viewCompanyUsersWithoutGoalsList") || false;

	const containerRef = useRef(null);

	/*
	 * Here we are getting the goals dashboard from a hidden div'd element in the tornado template that is generated server side.
	 * We are then cloning the element and appending it to the containerRef.current element.
	 * We're then hiding the original and using the clones element for display when the user navigates to the dashboard page.
	 */
	useEffect(() => {
		$("#tornado-template-content").show();
		containerRef.current &&
			$("#goals-dashboard").clone().attr("id", "goals-dashboard-copy").appendTo(containerRef.current);
		$("#goals-dashboard-copy").css("visibility", "visible");
		$("#tornado-template-content").hide();
	}, [page, isLoading, width]);

	if (isLoading) {
		return <ComponentLoader />;
	}

	// Here we are determining which Nav items can be used based on RBAC permissions
	const navItems = GOALS_SIDE_NAVIGATION_LINKS.filter((items) => {
		if (items.key === "list") {
			return userCanViewCompanyGoalsList;
		} else if (items.key === "dashboard") {
			return userCanViewGoalsOverview && ffGoalsDashboard;
		} else if (items.key === "alignment") {
			return userCanViewGoalsAlignment;
		} else if (items.key === "employees-without-goals") {
			return userCanViewCompanyUsersWithoutGoalsList;
		}
	});

	const pageName = page || navItems[0].key;

	return (
		<div>
			{pageName === GOALS_SIDE_NAVIGATION_LINKS[0].key && userCanViewCompanyGoalsList && (
				<GoalsList
					userCanBulkEditGoals={userCanBulkEditGoals}
					userCanExportToCsv={userCanExportGoalToCsv}
					navItems={navItems}
					userID={userID}
				/>
			)}
			{pageName === GOALS_SIDE_NAVIGATION_LINKS[1].key && userCanViewGoalsOverview && (
				<Overview navItems={navItems} containerRef={containerRef} />
			)}
			{pageName === GOALS_SIDE_NAVIGATION_LINKS[2].key && userCanViewGoalsAlignment && (
				<Alignment navItems={navItems} userID={userID} />
			)}
			{pageName === GOALS_SIDE_NAVIGATION_LINKS[3].key && userCanViewCompanyUsersWithoutGoalsList && (
				<EmployeesWithoutGoals navItems={navItems} />
			)}
		</div>
	);
};

export default CompanyGoalsPage;
